import { useEffect, useState, type MouseEvent as ReactMouseEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import AccountHeader from './components/AccountHeader';
import ProfileOverview from './components/ProfileOverview';
import ProfileEditor from './components/ProfileEditor';
import MyCourses from './components/MyCourses';
import MyBookings from './components/MyBookings';
import BookingPaymentModal from './components/BookingPaymentModal';
import MembershipOrders from './components/MembershipOrders';
import OrderHistory from './components/OrderHistory';
import OrderPaymentModal from './components/OrderPaymentModal';
import { useAuth } from '@/lib/auth';
import { api } from '@/lib/api';
import { adaptMembershipOrder, adaptMentorBooking, adaptMyCourse, adaptOrder } from '@/lib/adapters';
import MembershipPaymentModal from '@/components/payment/MembershipPaymentModal';
import MentorAccountPage from './mentor-page';

type TabType = 'profile' | 'profile-edit' | 'courses' | 'bookings' | 'orders' | 'memberships';

const AccountPage = () => {
  const [searchParams] = useSearchParams();
  const initialTab = (searchParams.get('tab') as TabType) || 'courses';
  const tabSet: TabType[] = ['profile', 'profile-edit', 'courses', 'bookings', 'orders', 'memberships'];
  const [activeTab, setActiveTab] = useState<TabType>(tabSet.includes(initialTab) ? initialTab : 'courses');
  const [courses, setCourses] = useState<ReturnType<typeof adaptMyCourse>[]>([]);
  const [bookings, setBookings] = useState<ReturnType<typeof adaptMentorBooking>[]>([]);
  const [orders, setOrders] = useState<ReturnType<typeof adaptOrder>[]>([]);
  const [membershipOrders, setMembershipOrders] = useState<ReturnType<typeof adaptMembershipOrder>[]>([]);
  const [activeBookingPaymentId, setActiveBookingPaymentId] = useState<string | null>(null);
  const [activeMembershipPaymentId, setActiveMembershipPaymentId] = useState<string | null>(null);
  const [activeOrderPaymentId, setActiveOrderPaymentId] = useState<string | null>(null);
  const { user, refreshUser } = useAuth();
  const isMentor = user?.userType === 2;

  const loadAccountData = () => {
    void Promise.all([
      api.getMyCourses({ pageCurrent: 1, pageSize: 20 }),
      api.getMyBookings({ pageCurrent: 1, pageSize: 20 }),
      api.getMyOrders({ pageCurrent: 1, pageSize: 20 }),
      api.getMyMembershipOrders({ pageCurrent: 1, pageSize: 20 })
    ]).then(([coursePage, bookingPage, orderPage, membershipOrderPage]) => {
      setCourses(coursePage.list.map(adaptMyCourse));
      setBookings(bookingPage.list.map(adaptMentorBooking));
      setOrders(orderPage.list.map(adaptOrder));
      setMembershipOrders(membershipOrderPage.list.map(adaptMembershipOrder));
    });
  };

  useEffect(() => {
    if (isMentor) {
      return;
    }
    loadAccountData();
  }, [isMentor]);

  const handleCancelBooking = (id: string) => {
    void api.cancelMentorBooking({ id }).then(() => {
      setBookings((current) =>
        current.map((item) =>
          item.id === id
            ? { ...item, status: 'closed', bookingStatus: 4, bookingStatusText: '已取消' }
            : item
        )
      );
    });
  };

  const handleContinueBookingPay = (id: string) => {
    setActiveBookingPaymentId(id);
  };

  const bookingStatusTextByCode = (status: number) => {
    if (status === 5) return '已完成';
    if (status === 4) return '已取消';
    if (status === 3) return '已確認';
    if (status === 2) return '待確認';
    return '待選時間';
  };

  const handleSetBookingSchedule = async ({ id, bookingDate, bookingTime }: { id: string; bookingDate: string; bookingTime: string }) => {
    await api.updateMentorBookingTime({
      id,
      bookingDate,
      bookingTime,
      bookingTimezone: 'HKT',
      preferTime: `${bookingDate} ${bookingTime}`
    });
    setBookings((current) =>
      current.map((item) => {
        if (item.id !== id) {
          return item;
        }
        const nextStatus = item.bookingStatus === 1 ? 2 : item.bookingStatus;
        return {
          ...item,
          bookingDate,
          bookingTime,
          bookingTimezone: 'HKT',
          preferTime: `${bookingDate} ${bookingTime}`,
          bookingStatus: nextStatus,
          bookingStatusText: bookingStatusTextByCode(nextStatus)
        };
      })
    );
  };

  const activeBooking = bookings.find((item) => item.id === activeBookingPaymentId) || null;
  const activeMembershipOrder = membershipOrders.find((item) => item.id === activeMembershipPaymentId) || null;
  const activeOrder = orders.find((item) => item.id === activeOrderPaymentId) || null;

  const tabs: { id: TabType; label: string; icon: string; count?: number }[] = [
    { id: 'profile', label: '個人信息', icon: 'ri-user-3-line' },
    { id: 'profile-edit', label: '編輯資料', icon: 'ri-edit-box-line' },
    { id: 'courses', label: '我的課程', icon: 'ri-book-open-line', count: courses.length },
    { id: 'bookings', label: '我的預約', icon: 'ri-calendar-check-line', count: bookings.length },
    { id: 'orders', label: '課程訂單', icon: 'ri-receipt-line', count: orders.length },
    { id: 'memberships', label: '會員訂單', icon: 'ri-vip-crown-line', count: membershipOrders.length }
  ];

  const handleMagneticMove = (event: ReactMouseEvent<HTMLButtonElement>) => {
    const target = event.currentTarget;
    const rect = target.getBoundingClientRect();
    const x = event.clientX - rect.left - rect.width / 2;
    const y = event.clientY - rect.top - rect.height / 2;
    target.style.setProperty('--tx', `${x * 0.06}px`);
    target.style.setProperty('--ty', `${y * 0.08}px`);
  };

  const handleMagneticLeave = (event: ReactMouseEvent<HTMLButtonElement>) => {
    const target = event.currentTarget;
    target.style.setProperty('--tx', '0px');
    target.style.setProperty('--ty', '0px');
  };

  if (isMentor) {
    return <MentorAccountPage />;
  }

  return (
    <div
      className="min-h-screen bg-[radial-gradient(circle_at_top,_#e0f2fe_0%,_#f8fafc_36%,_#eef2ff_100%)] font-sans account-static"
    >
      <Navbar />
      <div style={{ paddingTop: '64px' }}>
        <AccountHeader
          userName={user?.nickname || user?.email || 'NexusGo 用戶'}
          email={user?.email || ''}
          mobile={user?.mobile}
          avatar={user?.userHead}
          availableAmount={user?.availableAmount}
          coursesCount={courses.length}
          ordersCount={orders.length}
          member={user?.member}
          memberPlanName={user?.memberPlanName}
          memberExpireTime={user?.memberExpireTime}
        />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-5 sm:py-6">
          <div className="grid grid-cols-1 lg:grid-cols-[280px_minmax(0,1fr)] gap-5">
            <aside className="premium-frame premium-frame--sidebar lg:sticky lg:top-24 h-fit rounded-3xl bg-white/70 backdrop-blur-xl shadow-[0_18px_40px_-24px_rgba(15,23,42,0.5)] p-3">
              <div className="mb-2 px-3 py-2">
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-sky-600/90">Workspace</p>
                <p className="text-xs text-slate-500 mt-1">管理你的学习档案与交易记录</p>
              </div>
              <div className="space-y-1">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    onMouseMove={handleMagneticMove}
                    onMouseLeave={handleMagneticLeave}
                    className={`account-side-btn w-full flex items-center gap-3 px-3 py-3 text-sm font-semibold cursor-pointer transition-all rounded-2xl border ${
                      activeTab === tab.id
                        ? 'is-active text-white border-transparent bg-[linear-gradient(120deg,#0284c7_0%,#2563eb_55%,#7c3aed_100%)] shadow-[0_10px_24px_-12px_rgba(37,99,235,0.85)]'
                        : 'text-slate-600 border-transparent hover:text-slate-900 hover:bg-slate-50/90'
                    }`}
                  >
                    <i className={`${tab.icon} text-base ${activeTab === tab.id ? 'text-white' : 'text-sky-600'}`} />
                    <span className="flex-1 text-left">{tab.label}</span>
                    {tab.count !== undefined ? (
                      <span className={`text-[11px] font-bold px-1.5 py-0.5 rounded-full ${
                        activeTab === tab.id ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-500'
                      }`}>
                        {tab.count}
                      </span>
                    ) : null}
                    <span className="menu-shimmer" />
                  </button>
                ))}
              </div>
            </aside>

            <main className="premium-frame premium-frame--content rounded-3xl bg-white/72 backdrop-blur-xl shadow-[0_24px_55px_-26px_rgba(15,23,42,0.4)] p-3 sm:p-5">
              <div key={activeTab} className="account-content-switch">
                {activeTab === 'profile' && <ProfileOverview user={user} />}
                {activeTab === 'profile-edit' && (
                  <ProfileEditor
                    user={user}
                    onSaved={() => {
                      void refreshUser();
                    }}
                  />
                )}
                {activeTab === 'courses' && <MyCourses courses={courses} />}
                {activeTab === 'bookings' && <MyBookings bookings={bookings} onCancel={handleCancelBooking} onContinuePay={handleContinueBookingPay} onSetSchedule={handleSetBookingSchedule} />}
                {activeTab === 'orders' && <OrderHistory orders={orders} onContinuePay={(id) => setActiveOrderPaymentId(id)} />}
                {activeTab === 'memberships' && (
                  <MembershipOrders
                    orders={membershipOrders}
                    onContinuePay={(id) => setActiveMembershipPaymentId(id)}
                  />
                )}
              </div>
            </main>
          </div>
        </div>
      </div>
      {activeBooking ? (
        <BookingPaymentModal
          booking={activeBooking}
          onClose={() => setActiveBookingPaymentId(null)}
          onSuccess={({ bookingId, orderStatus, payTime }) => {
            setBookings((current) =>
              current.map((item) =>
                item.id === bookingId
                  ? {
                      ...item,
                      status: orderStatus === 2 ? 'paid' : item.status,
                      date: payTime || item.date,
                      payTime: payTime || item.payTime
                    }
                  : item
              )
            );
            setActiveBookingPaymentId(null);
          }}
        />
      ) : null}
      {activeMembershipOrder ? (
        <MembershipPaymentModal
          target={{
            mode: 'continue',
            orderNo: activeMembershipOrder.orderId,
            planName: activeMembershipOrder.title,
            planPrice: activeMembershipOrder.amount,
            originalPrice: activeMembershipOrder.amount
          }}
          onClose={() => setActiveMembershipPaymentId(null)}
          onSuccess={() => {
            void api.getMyMembershipOrders({ pageCurrent: 1, pageSize: 20 }).then((page) => {
              setMembershipOrders(page.list.map(adaptMembershipOrder));
            });
            void refreshUser();
            setActiveMembershipPaymentId(null);
          }}
        />
      ) : null}
      {activeOrder ? (
        <OrderPaymentModal
          order={activeOrder}
          onClose={() => setActiveOrderPaymentId(null)}
          onSuccess={({ orderId, orderStatus, payTime }) => {
            setOrders((current) =>
              current.map((item) =>
                item.id === orderId
                  ? {
                      ...item,
                      status: orderStatus === 2 ? 'paid' : item.status,
                      date: payTime || item.date,
                      payTime: payTime || item.payTime
                    }
                  : item
              )
            );
            setActiveOrderPaymentId(null);
          }}
        />
      ) : null}
      <Footer />
    </div>
  );
};

export default AccountPage;
